import {
	MAX_BUTTON_LABEL_LENGTH,
	MAX_RANDOM_BRANCHES,
	MIN_RANDOM_BRANCHES,
} from "@velizhanin-dev/bot-scenario"
import { Plus, Trash2 } from "lucide-react"
import { makeButtonId } from "@/entities/bot-builder"
import { Button } from "@/shared/ui/button"
import { IssueText } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import type { EditorProps } from "../editor-props"
import { parseOptionalNumber } from "../editor-props"
import { issuesAt } from "../issues-text"

interface BranchDraft {
	id: string
	label: string
	weight: number
}

export function RandomEditor({ config, onChange, issues }: EditorProps) {
	const branches = (Array.isArray(config.branches) ? config.branches : []) as BranchDraft[]
	const total = branches.reduce(
		(sum, branch) => sum + (Number.isFinite(branch.weight) ? branch.weight : 0),
		0,
	)
	const setBranches = (next: BranchDraft[]) => onChange({ ...config, branches: next })
	return (
		<div className="space-y-4">
			<p className="text-sm text-muted-foreground">
				Каждый пользователь попадает в одну ветку случайно, с вероятностью по весу. Удобно для
				A/B-теста текстов и офферов.
			</p>
			{branches.map((branch, index) => (
				<div key={branch.id} className="grid grid-cols-[1fr_5rem_auto] items-center gap-1">
					<Input
						aria-label={`Название ветки ${index + 1}`}
						placeholder="Например, Вариант А"
						maxLength={MAX_BUTTON_LABEL_LENGTH}
						value={branch.label}
						onChange={(event) =>
							setBranches(
								branches.map((item, i) =>
									i === index ? { ...item, label: event.target.value } : item,
								),
							)
						}
					/>
					<Input
						type="number"
						min={1}
						max={100}
						aria-label={`Вес ветки ${index + 1}`}
						placeholder="50"
						value={Number.isFinite(branch.weight) ? branch.weight : ""}
						onChange={(event) =>
							setBranches(
								branches.map((item, i) =>
									i === index
										? { ...item, weight: parseOptionalNumber(event.target.value) ?? Number.NaN }
										: item,
								),
							)
						}
					/>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						aria-label={`Удалить ветку ${index + 1}`}
						disabled={branches.length <= MIN_RANDOM_BRANCHES}
						onClick={() => setBranches(branches.filter((_, i) => i !== index))}
					>
						<Trash2 />
					</Button>
					<p className="col-span-3 -mt-1 text-xs text-muted-foreground">
						{total > 0 && Number.isFinite(branch.weight)
							? `${Math.round((branch.weight / total) * 100)}% пользователей`
							: ""}
					</p>
				</div>
			))}
			<IssueText issues={issuesAt(issues, "branches", true)} />
			{branches.length < MAX_RANDOM_BRANCHES ? (
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={() =>
						setBranches([
							...branches,
							{ id: makeButtonId(), label: String.fromCharCode(65 + branches.length), weight: 50 },
						])
					}
				>
					<Plus />
					Ветка
				</Button>
			) : null}
		</div>
	)
}
